import React, { useState, useEffect } from "react";
import { FiFilter, FiX, FiCalendar } from "react-icons/fi";
import {
  FilterButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  FilterSection,
  FilterSectionTitle,
  FilterTypeSelect,
  DateInput,
  FilterLabel,
  FilterActions,
  ActionButton,
  ActiveFiltersBadge,
  ActiveFilterChip,
} from "../styles/MobileDateFilters.styles.js";
import {
  StatusFilterContainer,
  StatusFilterSelect,
} from "../styles/Layoutstyle";

const MobileDateFilters = ({
  dateFilters,
  onDateFilterChange,
  onStatusFilterChange,
  statusFilter,
  availableStatuses,
  isDarkMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Date field configurations
  const dateFields = [
    {
      fieldName: "orderDate",
      label: "Order Date",
      icon: <FiCalendar />,
    },
    {
      fieldName: "accuzip_startTime",
      label: "Accuzip Start Time",
      icon: <FiCalendar />,
    },
    {
      fieldName: "accuzip_endTime",
      label: "Accuzip End Time",
      icon: <FiCalendar />,
    },
    {
      fieldName: "streetViewCompletionDate",
      label: "Completion Date",
      icon: <FiCalendar />,
    },
  ];

  // Count active filters
  const activeFiltersCount = Object.keys(dateFilters || {}).filter(
    (key) => dateFilters[key] && dateFilters[key].value1
  ).length;

  // Local state for each filter
  const [localFilters, setLocalFilters] = useState({});

  // Initialize local filters from props
  useEffect(() => {
    if (isOpen) {
      const initialFilters = {};
      dateFields.forEach((field) => {
        const currentFilter = dateFilters?.[field.fieldName];
        initialFilters[field.fieldName] = {
          type: currentFilter?.type || "before",
          value1: currentFilter?.value1 || "",
          value2: currentFilter?.value2 || "",
        };
      });
      setLocalFilters(initialFilters);
    }
  }, [isOpen, dateFilters]);

  const handleFilterChange = (fieldName, field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [field]: value,
      },
    }));
  };

  const handleApply = () => {
    // Apply all filters
    dateFields.forEach((field) => {
      const filter = localFilters[field.fieldName];
      if (filter && filter.value1) {
        onDateFilterChange(field.fieldName, {
          type: filter.type,
          value1: filter.value1,
          value2: filter.type === "between" ? filter.value2 : undefined,
        });
      } else {
        // Clear filter if no value
        onDateFilterChange(field.fieldName, null);
      }
    });
    setIsOpen(false);
  };

  const handleClearAll = () => {
    // Clear all filters
    dateFields.forEach((field) => {
      onDateFilterChange(field.fieldName, null);
    });
    setIsOpen(false);
  };

  const formatFilterDisplay = (filter) => {
    if (!filter || !filter.value1) return null;
    const typeLabels = {
      before: "Before",
      after: "After",
      between: "Between",
    };
    let text = `${typeLabels[filter.type]} ${filter.value1}`;
    if (filter.type === "between" && filter.value2) {
      text += ` and ${filter.value2}`;
    }
    return text;
  };

  return (
    <>
      <FilterButton onClick={() => setIsOpen(true)}>
        <FiFilter />
        Date Filters
        {activeFiltersCount > 0 && (
          <span className="badge">{activeFiltersCount}</span>
        )}
      </FilterButton>

      {isOpen && (
        <ModalOverlay onClick={() => setIsOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} isDarkMode={isDarkMode}>
            <ModalHeader isDarkMode={isDarkMode}>
              <ModalTitle isDarkMode={isDarkMode}>Date Filters</ModalTitle>
              <CloseButton onClick={() => setIsOpen(false)} isDarkMode={isDarkMode}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <StatusFilterContainer>
              <ModalTitle style={{ padding: "15px 0" }} isDarkMode={isDarkMode}>
                order Status Filter
              </ModalTitle>
              <StatusFilterSelect
                style={{
                  marginBottom: "15px",
                  background: isDarkMode ? '#1a202c' : 'white',
                  color: isDarkMode ? '#e2e8f0' : '#333',
                  border: isDarkMode ? '1px solid #4a5568' : '1px solid #e1e5ee',
                }}
                value={statusFilter}
                onChange={onStatusFilterChange}
              >
                <option value="">All Statuses</option>
                {availableStatuses?.map((status) => (
                  <option key={status} value={status}>
                    {status.split(/[-_]/).length > 1
                      ? status.split(/[-_]/).slice(1).join("-")
                      : status}
                  </option>
                ))}
              </StatusFilterSelect>
            </StatusFilterContainer>

            {dateFields.map((field) => {
              const filter = localFilters[field.fieldName] || {
                type: "before",
                value1: "",
                value2: "",
              };
              const currentFilter = dateFilters?.[field.fieldName];

              return (
                <FilterSection key={field.fieldName}>
                  <FilterSectionTitle isDarkMode={isDarkMode}>
                    {field.icon}
                    {field.label}
                  </FilterSectionTitle>

                  <FilterLabel isDarkMode={isDarkMode}>Filter Type</FilterLabel>
                  <FilterTypeSelect
                    value={filter.type}
                    onChange={(e) =>
                      handleFilterChange(
                        field.fieldName,
                        "type",
                        e.target.value
                      )
                    }
                    isDarkMode={isDarkMode}
                  >
                    <option value="before">Before than</option>
                    <option value="after">After</option>
                    <option value="between">Between</option>
                  </FilterTypeSelect>

                  <FilterLabel isDarkMode={isDarkMode}>
                    {filter.type === "between" ? "Start Date" : "Date"}
                  </FilterLabel>
                  <DateInput
                    type="date"
                    value={filter.value1}
                    onChange={(e) =>
                      handleFilterChange(
                        field.fieldName,
                        "value1",
                        e.target.value
                      )
                    }
                    isDarkMode={isDarkMode}
                  />

                  {filter.type === "between" && (
                    <>
                      <FilterLabel isDarkMode={isDarkMode}>End Date</FilterLabel>
                      <DateInput
                        type="date"
                        value={filter.value2}
                        onChange={(e) =>
                          handleFilterChange(
                            field.fieldName,
                            "value2",
                            e.target.value
                          )
                        }
                        min={filter.value1}
                        isDarkMode={isDarkMode}
                      />
                    </>
                  )}

                  {currentFilter && currentFilter.value1 && (
                    <ActiveFiltersBadge isDarkMode={isDarkMode}>
                      <ActiveFilterChip>
                        Active: {formatFilterDisplay(currentFilter)}
                      </ActiveFilterChip>
                    </ActiveFiltersBadge>
                  )}
                </FilterSection>
              );
            })}

            {/*  Action buttons */}
            <FilterActions isDarkMode={isDarkMode}>
              <ActionButton className="clear" onClick={handleClearAll} isDarkMode={isDarkMode}>
                Clear All
              </ActionButton>
              <ActionButton className="apply" onClick={handleApply} isDarkMode={isDarkMode}>
                Apply Filters
              </ActionButton>
            </FilterActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default MobileDateFilters;
